import styles from './AccordionCarousel.module.scss';

import cx from 'classnames';
import React, { CSSProperties, FC, HTMLAttributes, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { CarouselAssembly, CarouselType } from 'api/contentful/contentTypes.cloverdotcom';
import { CircleArrowButton } from 'components/CircleArrowButton/CircleArrowButton';
import { ContentLinkV2 } from 'components/ContentLinkV2/ContentLinkV2';
import { SubjectImage } from 'components/SubjectImage/SubjectImage';
import { TextV2 } from 'components/TextV2/TextV2';
import { TextWithArrow } from 'components/TextWithArrow/TextWithArrow';
import { usePan } from 'hooks/usePan';
import { FlatEntry } from 'services/cms/flattenContentful';
import { toEntry } from 'services/cms/typesUtil';
import { isAriaCondition } from 'utils/isAriaCondition';
import { setAriaLive } from 'utils/setAriaLive';
import { FadeToastUp } from 'components/Animation/FadeToastUp/FadeToastUp';
import { useBreakpoints } from 'hooks/useBreakpoints';
import { CaseStudyItem } from '../CaseStudyItem/CaseStudyItem';
import { RichBlurb } from 'components/RichBlurb/RichBlurb';

type AccordionCarouselProps = HTMLAttributes<HTMLElement> & {
  entry?: FlatEntry<CarouselAssembly>;
};

export const AccordionCarousel: FC<AccordionCarouselProps> = ({ entry = {}, className, ...props }) => {
  const { t } = useTranslation();
  const { browser } = useBreakpoints();
  const rootRef = useRef<HTMLElement>(null);
  const ariaLiveRef = useRef<HTMLDivElement>(null);
  const [expandedIndex, setExpandedIndex] = useState(0);
  const items = entry.items?.filter(Boolean) ?? [];
  const isFirstIndex = expandedIndex === 0;
  const isLastIndex = expandedIndex === items.length - 1;
  const activeItem = items[expandedIndex];
  const isDefaultVariation = entry.carouselType === CarouselType.ACCORDION;
  const isCaseStudyVariation = entry.carouselType === CarouselType.ACCORDION_CASE_STUDY;
  const isDesktop = browser.is.desktop;
  const isMobile = browser.is.mobile;
  const isTablet = browser.is.tablet;

  const { pointerRef, ...pointerEvents } = usePan({
    cssVarEl: isDesktop ? rootRef : undefined,
    isFirstIndex,
    isLastIndex,
    onPanNext: onNext,
    onPanPrev: onPrev,
  });

  useEffect(() => {
    if (!activeItem || !ariaLiveRef.current) return;
    setAriaLive(ariaLiveRef.current, `Show ${activeItem.title}`);
  }, [activeItem]);

  if (!items.length) return null;

  function onPrev() {
    if (isFirstIndex) return;
    setExpandedIndex(expandedIndex - 1);
  }

  function onNext() {
    if (isLastIndex) return;
    setExpandedIndex(expandedIndex + 1);
  }

  return (
    <section
      className={cx(
        styles.accordion,
        isCaseStudyVariation && styles.isCaseStudy,
        isCaseStudyVariation && items.length > 4 && styles.compact,
        className,
      )}
      ref={rootRef}
      style={{ '--active-index': expandedIndex } as CSSProperties}
      {...props}
    >
      <FadeToastUp>
        <TextV2
          className={styles.heading}
          value={entry.title}
          tagName={entry.headingTagName ?? 'h2'}
          variant={isDefaultVariation ? 'modern-h2' : 'modern-h3'}
        />
      </FadeToastUp>
      <FadeToastUp>
        <div className={styles.accordionItemContainer} {...pointerEvents}>
          {items.map((item, index) => {
            const isActive = index === expandedIndex;
            const isNextActive = Math.abs(index - expandedIndex) === 1;
            let computedTabIndex = undefined;
            if (isMobile || isTablet) {
              computedTabIndex = isActive ? 0 : -1;
            }

            return (
              <div
                key={item.id}
                className={cx(styles.accordionItem, {
                  [styles.expandedAccordionItem]: isActive,
                  [styles.nextExpandedAccordionItem]: isNextActive,
                })}
                role="group"
                style={{ '--diff-index': index - expandedIndex } as CSSProperties}
              >
                <SubjectImage className={styles.accordionMedia} entry={item} />
                <div className={styles.accordionGradient} />
                <button
                  aria-disabled={isAriaCondition(isActive)}
                  aria-label={item.title}
                  className={cx(styles.accordionLink, isActive && styles.accordionLinkActive)}
                  tabIndex={computedTabIndex}
                  onClick={event => {
                    event.preventDefault();
                    if (isActive || pointerRef.current.flag) return;
                    setExpandedIndex(index);
                  }}
                  type="button"
                />
                <div className={styles.overlayContainer}>
                  {isDefaultVariation && (
                    <>
                      <SubjectImage
                        className={styles.overlayIcon}
                        entry={item.link}
                        alt=""
                        aria-hidden="true"
                      />
                      <TextV2
                        aria-hidden={isAriaCondition(!isActive)}
                        className={styles.overlayText}
                        tagName="h3"
                        value={item.title}
                        variant="modern-h4"
                      />
                    </>
                  )}
                  {isCaseStudyVariation && <CaseStudyItem entry={item} isActive={isActive} />}
                </div>
              </div>
            );
          })}
        </div>
      </FadeToastUp>
      {activeItem && (
        <FadeToastUp>
          <div className={styles.footerContainer}>
            {isDefaultVariation && (
              <div className={styles.footerExpandedItem}>
                <TextV2 className={styles.footerExpandedItemText} value={activeItem.blurb} tagName="p" />
                <RichBlurb className={styles.footerExpandedItemText} document={activeItem.richBlurb} />
                {activeItem.link && (
                  <ContentLinkV2
                    className={styles.footerExpandedItemLink}
                    entry={toEntry(activeItem.link)}
                    hideTitle
                  >
                    <TextWithArrow>{activeItem.link.title}</TextWithArrow>
                  </ContentLinkV2>
                )}
              </div>
            )}
            <div className={styles.carouselButtons}>
              <CircleArrowButton
                aria-disabled={isAriaCondition(isFirstIndex)}
                disabled={isFirstIndex}
                aria-label={t('carousel.previous-item')}
                className={styles.carouselButton}
                onClick={onPrev}
                variant="left"
              />
              <CircleArrowButton
                aria-disabled={isAriaCondition(isLastIndex)}
                disabled={isLastIndex}
                aria-label={t('carousel.next-item')}
                className={styles.carouselButton}
                onClick={onNext}
                variant="right"
              />
            </div>
          </div>
        </FadeToastUp>
      )}
      <div ref={ariaLiveRef} />
    </section>
  );
};
